import { useEffect, useState, type ReactNode } from "react";
import { AuthLayout, Spinner } from "../components/shell/AuthLayout";
import { DiscordBindFlow } from "../components/DiscordBindFlow";
import { Button } from "../components/ui/Button";
import { discordBindStatus } from "../lib/api";
import { discordConfigured, isLocalDev } from "../lib/discord";
import { useT, type UiLang } from "../lib/i18n";
import { stashLandingHash } from "../lib/landing";
import { bindSkippedThisSession, bootSession, clearSession, isDiscordTicket, isExpired, skipBindThisSession } from "../lib/session";

/**
 * 没绑 Discord 就用不了系统（leo 2026-10-19）。
 *
 * 包在整个配置台外面：没绑的人只看到这一页，配置台本身不挂载、不拉任何配置。
 * 用账号密码或 super-admin 进来的人在这里绑；绑完用 Discord 登录一次就进去了——
 * Discord 登录的票自带 `discord:` 前缀，服务端直接算绑过。
 *
 * 绑没绑是服务端推断的（notify-hub 没有按人查绑定状态的接口，见 admin/third-auth.ts 的 bindStatus），
 * 所以「绑过但一直用账号密码登录」的人也会停在这页；页面上让他点 Discord 登录即可，不用重绑。
 *
 * 注册不了 Discord 的人可以点「下次再说」先进去，只管这一次登录，下次登录还会拦（见 lib/session.ts 的 skipBindThisSession）。
 *
 * 本地开发（localhost）不拦，见 lib/discord.ts 的 isLocalDev。
 *
 * 这是前端的门：接口层不拦（脚本、联调工具都拿管端票直接打接口，在中间件里拦会把它们全打断）。
 */
export function DiscordBindGuard({ uiLang, onUiLang, children }: { uiLang: UiLang; onUiLang: (l: UiLang) => void; children: ReactNode }) {
  const t = useT();
  // bootSession 可以重复调：第一次已把地址栏里的票存起来，之后都从存储里读
  const [session] = useState(bootSession);
  const loggedIn = !!session.token && !isExpired(session);
  const [state, setState] = useState<"checking" | "ok" | "unbound" | "failed">(
    loggedIn && discordConfigured() && !isLocalDev() && !isDiscordTicket(session.token) && !bindSkippedThisSession() ? "checking" : "ok",
  );
  const [err, setErr] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (state !== "checking") return;
    let alive = true;
    discordBindStatus()
      .then((s) => { if (alive) setState(!s.configured || s.bound ? "ok" : "unbound"); })
      .catch((e: unknown) => { if (alive) { setErr((e as Error).message); setState("failed"); } });
    return () => { alive = false; };
  }, [state, attempt]);

  // 没登录 / 这个环境没接 Discord：照常交给配置台（没登录时它自己画登录页）
  if (state === "ok") return <>{children}</>;
  if (state === "checking") {
    return (
      <AuthLayout uiLang={uiLang} onUiLang={onUiLang}>
        <div className="flex justify-center py-8 text-ink-placeholder"><Spinner className="h-7 w-7" /></div>
      </AuthLayout>
    );
  }
  if (state === "failed") {
    // 查不到就别放行，也别装作没绑：说清楚是查询失败，给重试
    return (
      <AuthLayout uiLang={uiLang} onUiLang={onUiLang}>
        <h1 className="text-lg font-semibold text-ink">{t("bind.statusFailed")}</h1>
        <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-800">{err}</p>
        <RetryRow onRetry={() => { setErr(null); setAttempt((n) => n + 1); setState("checking"); }} />
      </AuthLayout>
    );
  }
  return (
    <BindRequired uiLang={uiLang} onUiLang={onUiLang} who={session.operator}
      onSkip={() => { skipBindThisSession(); setState("ok"); }} onBound={() => setState("ok")} />
  );
}

function RetryRow({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="mt-6 flex gap-3">
      <Button tone="primary" className="!h-10 flex-1 !rounded-lg" onClick={onRetry}>{t("common.retry")}</Button>
      <LogoutButton />
    </div>
  );
}

function LogoutButton() {
  const t = useT();
  return (
    <Button className="!h-10 flex-1 !rounded-lg" onClick={() => { clearSession(); location.replace("/"); }}>
      {t("bind.switchAccount")}
    </Button>
  );
}

function BindRequired({ uiLang, onUiLang, who, onSkip, onBound }: {
  uiLang: UiLang; onUiLang: (l: UiLang) => void; who: string | null; onSkip: () => void; onBound: () => void;
}) {
  const t = useT();
  // 记下原来要去的页面：有人会改走 Discord 私信里的登录链接回来，那条路不经过我们的按钮
  useEffect(() => {
    if (window.location.hash.length > 1) stashLandingHash(window.location.hash);
  }, []);

  /*
   * 越简单越好（leo 2026-10-19）：两步——进服务器、把命令私聊发给机器人。界面自己画（DiscordBindFlow），
   * 不再嵌 notify-hub 的 iframe。绑上之后服务端已记成已绑，这里停一秒让人看到「绑定成功」就直接进系统，
   * 不用再走一趟 Discord 登录。
   */
  return (
    <AuthLayout uiLang={uiLang} onUiLang={onUiLang} width={460}>
      <h1 className="text-xl font-semibold text-ink">{t("bind.title")}</h1>
      <p className="mt-1.5 text-sm leading-relaxed text-ink-secondary">{t("bind.body", { who: who ?? "—" })}</p>
      <div className="mt-6">
        <DiscordBindFlow onBound={() => setTimeout(onBound, 1200)} />
      </div>
      {/* 两个出口刻意做小：主路径是绑定 */}
      <div className="mt-7 flex items-center justify-center gap-3 text-xs text-ink-placeholder">
        <button type="button" onClick={() => { clearSession(); location.replace("/"); }} className="hover:text-ink-regular">{t("bind.switchAccount")}</button>
        <span aria-hidden>·</span>
        <button type="button" onClick={onSkip} className="hover:text-ink-regular">{t("bind.skip")}</button>
      </div>
    </AuthLayout>
  );
}
